import { jsx as _jsx } from "react/jsx-runtime";
// src/lib/i18n/LanguageContext.tsx
// Language context for bilingual support (EN/ES)
import { createContext, useContext, useState, useCallback, useMemo, useEffect, } from "react";
import { useLocation, useNavigate } from "react-router-dom";
// Language configurations
export const LANGUAGES = {
    en: {
        code: "en",
        label: "English",
        nativeLabel: "English",
        hrefLang: "en-US",
    },
    es: {
        code: "es",
        label: "Spanish",
        nativeLabel: "Español",
        hrefLang: "es",
    },
};
// SEO service slug mappings (English ↔ Spanish)
const seoServiceSlugMap = {
    // English to Spanish
    "cannabis-defense": "defensa-de-cannabis",
    "car-accident": "accidente-de-auto",
    "criminal-defense": "defensa-criminal",
    "domestic-violence-defense": "defensa-por-violencia-domestica",
    "drug-charges-defense": "defensa-por-cargos-de-drogas",
    "dui-defense": "defensa-de-dui",
    "federal-criminal-defense": "defensa-criminal-federal",
    "gun-charges-defense": "defensa-por-cargos-de-armas",
    "medical-malpractice": "negligencia-medica",
    "motorcycle-accident": "accidente-de-motocicleta",
    "personal-injury": "lesiones-personales",
    "probation-violation": "violacion-de-libertad-condicional",
    "sex-crimes-defense": "defensa-por-delitos-sexuales",
    "slip-and-fall": "resbalones-y-caidas",
    "truck-accident": "accidente-de-camion",
    "violent-crimes-defense": "defensa-por-crimenes-violentos",
    "wrongful-death": "muerte-injusta",
    // Spanish to English (reverse mappings)
    "defensa-de-cannabis": "cannabis-defense",
    "accidente-de-auto": "car-accident",
    "defensa-criminal": "criminal-defense",
    "defensa-por-violencia-domestica": "domestic-violence-defense",
    "defensa-por-cargos-de-drogas": "drug-charges-defense",
    "defensa-de-dui": "dui-defense",
    "defensa-criminal-federal": "federal-criminal-defense",
    "defensa-por-cargos-de-armas": "gun-charges-defense",
    "negligencia-medica": "medical-malpractice",
    "accidente-de-motocicleta": "motorcycle-accident",
    "lesiones-personales": "personal-injury",
    "violacion-de-libertad-condicional": "probation-violation",
    "defensa-por-delitos-sexuales": "sex-crimes-defense",
    "resbalones-y-caidas": "slip-and-fall",
    "accidente-de-camion": "truck-accident",
    "defensa-por-crimenes-violentos": "violent-crimes-defense",
    "muerte-injusta": "wrongful-death",
};
// English service slugs (used to detect SEO pages)
const englishServiceSlugs = [
    "cannabis-defense",
    "car-accident",
    "criminal-defense",
    "domestic-violence-defense",
    "drug-charges-defense",
    "dui-defense",
    "federal-criminal-defense",
    "gun-charges-defense",
    "medical-malpractice",
    "motorcycle-accident",
    "personal-injury",
    "probation-violation",
    "sex-crimes-defense",
    "slip-and-fall",
    "truck-accident",
    "violent-crimes-defense",
    "wrongful-death",
];
// Spanish service slugs
const spanishServiceSlugs = [
    "defensa-de-cannabis",
    "defensa-criminal",
    "defensa-de-dui",
    "defensa-por-cargos-de-drogas",
    "lesiones-personales",
    "negligencia-medica",
];
// English to Spanish route mappings
const enToEsRouteMap = {
    "/": "/es",
    "/about": "/es/sobre-nosotros",
    "/blog": "/es/blog",
    "/contact": "/es/contacto",
    "/practice-areas": "/es/areas-de-practica",
    "/practice-areas/criminal-defense": "/es/areas-de-practica/defensa-criminal",
    "/practice-areas/cannabis-defense": "/es/areas-de-practica/defensa-cannabis",
    "/practice-areas/personal-injury": "/es/areas-de-practica/lesiones-personales",
    "/practice-areas/drug-charges": "/es/areas-de-practica/cargos-de-drogas",
    "/practice-areas/gun-charges": "/es/areas-de-practica/cargos-de-armas",
    "/practice-areas/violent-crimes": "/es/areas-de-practica/crimenes-violentos",
    "/locations": "/es/ubicaciones",
    "/locations/washington-dc": "/es/ubicaciones/washington-dc",
    "/locations/montgomery-county": "/es/ubicaciones/condado-montgomery-md",
    "/locations/montgomery-county-md": "/es/ubicaciones/condado-montgomery-md",
    "/locations/prince-georges-county": "/es/ubicaciones/condado-prince-georges-md",
    "/locations/prince-georges-county-md": "/es/ubicaciones/condado-prince-georges-md",
    "/privacy-policy": "/privacy-policy", // Legal pages stay the same
    "/terms-of-service": "/terms-of-service", // Legal pages stay the same
};
// Spanish to English route mappings (explicit to avoid collisions)
const esToEnRouteMap = {
    "/es": "/", // Spanish home maps to English home
    "/es/sobre-nosotros": "/about",
    "/es/blog": "/blog",
    "/es/contacto": "/contact",
    "/es/areas-de-practica": "/practice-areas",
    "/es/areas-de-practica/defensa-criminal": "/practice-areas/criminal-defense",
    "/es/areas-de-practica/defensa-cannabis": "/practice-areas/cannabis-defense",
    "/es/areas-de-practica/lesiones-personales": "/practice-areas/personal-injury",
    "/es/areas-de-practica/cargos-de-drogas": "/practice-areas/drug-charges",
    "/es/areas-de-practica/cargos-de-armas": "/practice-areas/gun-charges",
    "/es/areas-de-practica/crimenes-violentos": "/practice-areas/violent-crimes",
    "/es/ubicaciones": "/locations",
    "/es/ubicaciones/washington-dc": "/locations/washington-dc",
    "/es/ubicaciones/condado-montgomery-md": "/locations/montgomery-county-md",
    "/es/ubicaciones/condado-prince-georges-md": "/locations/prince-georges-county-md",
    "/privacy-policy": "/privacy-policy", // Legal pages stay the same
    "/terms-of-service": "/terms-of-service", // Legal pages stay the same
};
// Check if a path is an SEO landing page
function isSeoLandingPage(path) {
    const normalizedPath = path.replace(/\/$/, "");
    const segments = normalizedPath.split("/").filter(Boolean);
    if (segments.length !== 2) {
        return { isSeo: false, serviceSlug: "", citySlug: "", isSpanish: false };
    }
    const [serviceSlug, citySlug] = segments;
    // Check if it's an English SEO page
    if (englishServiceSlugs.includes(serviceSlug)) {
        return { isSeo: true, serviceSlug, citySlug, isSpanish: false };
    }
    // Check if it's a Spanish SEO page
    if (spanishServiceSlugs.includes(serviceSlug)) {
        return { isSeo: true, serviceSlug, citySlug, isSpanish: true };
    }
    return { isSeo: false, serviceSlug: "", citySlug: "", isSpanish: false };
}
// Fallback practice area pages when SEO page doesn't exist
const practiceAreaFallbacks = {
    "cannabis-defense": { en: "/practice-areas/cannabis-defense", es: "/es/areas-de-practica/defensa-cannabis" },
    "defensa-de-cannabis": { en: "/practice-areas/cannabis-defense", es: "/es/areas-de-practica/defensa-cannabis" },
    "criminal-defense": { en: "/practice-areas/criminal-defense", es: "/es/areas-de-practica/defensa-criminal" },
    "defensa-criminal": { en: "/practice-areas/criminal-defense", es: "/es/areas-de-practica/defensa-criminal" },
    "dui-defense": { en: "/practice-areas/criminal-defense", es: "/es/areas-de-practica/defensa-criminal" },
    "defensa-de-dui": { en: "/practice-areas/criminal-defense", es: "/es/areas-de-practica/defensa-criminal" },
    "drug-charges-defense": { en: "/practice-areas/drug-charges", es: "/es/areas-de-practica/cargos-de-drogas" },
    "defensa-por-cargos-de-drogas": { en: "/practice-areas/drug-charges", es: "/es/areas-de-practica/cargos-de-drogas" },
    "personal-injury": { en: "/practice-areas/personal-injury", es: "/es/areas-de-practica/lesiones-personales" },
    "lesiones-personales": { en: "/practice-areas/personal-injury", es: "/es/areas-de-practica/lesiones-personales" },
    "medical-malpractice": { en: "/practice-areas/personal-injury", es: "/es/areas-de-practica/lesiones-personales" },
    "negligencia-medica": { en: "/practice-areas/personal-injury", es: "/es/areas-de-practica/lesiones-personales" },
    "gun-charges-defense": { en: "/practice-areas/gun-charges", es: "/es/areas-de-practica/cargos-de-armas" },
    "violent-crimes-defense": { en: "/practice-areas/violent-crimes", es: "/es/areas-de-practica/crimenes-violentos" },
    // Services without direct practice area pages fallback to main practice areas
    "car-accident": { en: "/practice-areas/personal-injury", es: "/es/areas-de-practica/lesiones-personales" },
    "motorcycle-accident": { en: "/practice-areas/personal-injury", es: "/es/areas-de-practica/lesiones-personales" },
    "truck-accident": { en: "/practice-areas/personal-injury", es: "/es/areas-de-practica/lesiones-personales" },
    "slip-and-fall": { en: "/practice-areas/personal-injury", es: "/es/areas-de-practica/lesiones-personales" },
    "wrongful-death": { en: "/practice-areas/personal-injury", es: "/es/areas-de-practica/lesiones-personales" },
    "domestic-violence-defense": { en: "/practice-areas/criminal-defense", es: "/es/areas-de-practica/defensa-criminal" },
    "federal-criminal-defense": { en: "/practice-areas/criminal-defense", es: "/es/areas-de-practica/defensa-criminal" },
    "probation-violation": { en: "/practice-areas/criminal-defense", es: "/es/areas-de-practica/defensa-criminal" },
    "sex-crimes-defense": { en: "/practice-areas/criminal-defense", es: "/es/areas-de-practica/defensa-criminal" },
};
// Get alternate SEO page URL for language switching
function getAlternateSeoUrl(serviceSlug, citySlug, targetLang) {
    const mappedService = seoServiceSlugMap[serviceSlug];
    if (!mappedService) {
        // No translation available for this service
        return null;
    }
    return `/${mappedService}/${citySlug}`;
}
// Get fallback URL when SEO page doesn't exist in target language
function getFallbackUrl(serviceSlug, targetLang) {
    const fallback = practiceAreaFallbacks[serviceSlug];
    if (fallback) {
        return targetLang === "es" ? fallback.es : fallback.en;
    }
    // Ultimate fallback
    return targetLang === "es" ? "/es/areas-de-practica" : "/practice-areas";
}
const LanguageContext = createContext(null);
export const LanguageProvider = ({ children, defaultLanguage = "en", }) => {
    const location = useLocation();
    const navigate = useNavigate();
    // Detect language from URL
    const detectLanguage = useCallback(() => {
        // First check if it's an SEO landing page
        const seoInfo = isSeoLandingPage(location.pathname);
        if (seoInfo.isSeo) {
            return seoInfo.isSpanish ? "es" : "en";
        }
        // Standard route detection
        if (location.pathname.startsWith("/es"))
            return "es";
        return "en";
    }, [location.pathname]);
    const [language, setLanguageState] = useState(detectLanguage);
    // Update language when URL changes
    useEffect(() => {
        const detected = detectLanguage();
        if (detected !== language) {
            setLanguageState(detected);
        }
    }, [location.pathname, detectLanguage, language]);
    // Get alternate URL for language switching
    const getAlternateUrl = useCallback((path, targetLang) => {
        // Normalize path (remove trailing slash unless it's root)
        const normalizedPath = path === "/" || path === "/es" ? path : path.replace(/\/$/, "");
        // Check if it's an SEO landing page
        const seoInfo = isSeoLandingPage(normalizedPath);
        if (seoInfo.isSeo) {
            const currentLang = seoInfo.isSpanish ? "es" : "en";
            // If already at target language, return current path
            if (currentLang === targetLang)
                return normalizedPath;
            // Get the alternate SEO URL
            const alternateUrl = getAlternateSeoUrl(seoInfo.serviceSlug, seoInfo.citySlug, targetLang);
            if (alternateUrl) {
                return alternateUrl;
            }
            // No direct translation - use practice area fallback
            return getFallbackUrl(seoInfo.serviceSlug, targetLang);
        }
        // Standard route handling
        const currentLang = normalizedPath.startsWith("/es") ? "es" : "en";
        // If already at target language, return current path
        if (currentLang === targetLang)
            return normalizedPath;
        if (targetLang === "es") {
            // English to Spanish - use explicit map
            const mappedPath = enToEsRouteMap[normalizedPath];
            if (mappedPath)
                return mappedPath;
            // Fallback: if path not in map, return Spanish home
            console.warn(`No Spanish route mapping found for: ${normalizedPath}. Redirecting to /es`);
            return "/es";
        }
        else {
            // Spanish to English - use explicit map
            const mappedPath = esToEnRouteMap[normalizedPath];
            if (mappedPath)
                return mappedPath;
            // Fallback: if path not in reverse map, return English home
            console.warn(`No English route mapping found for: ${normalizedPath}. Redirecting to /`);
            return "/";
        }
    }, []);
    // Switch language and navigate
    const setLanguage = useCallback((lang) => {
        // Don't navigate if already at target language
        if (lang === language)
            return;
        // Get the new path based on current location
        const newPath = getAlternateUrl(location.pathname, lang);
        // Validate that newPath is valid
        if (!newPath || newPath.trim() === "") {
            console.error("Invalid navigation path generated", {
                currentPath: location.pathname,
                targetLang: lang,
            });
            return;
        }
        // Update state and navigate
        setLanguageState(lang);
        navigate(newPath, { replace: true });
    }, [language, location.pathname, getAlternateUrl, navigate]);
    // Toggle between languages
    const toggleLanguage = useCallback(() => {
        setLanguage(language === "en" ? "es" : "en");
    }, [language, setLanguage]);
    // Basic translation function (placeholder for full i18n)
    // In production, this would use react-i18next or similar
    const t = useCallback((key) => {
        // For now, just return the key
        // This will be expanded with proper translation files
        return key;
    }, []);
    const value = useMemo(() => ({
        language,
        setLanguage,
        toggleLanguage,
        config: LANGUAGES[language],
        t,
        getAlternateUrl,
        isSpanish: language === "es",
    }), [language, setLanguage, toggleLanguage, t, getAlternateUrl]);
    return (_jsx(LanguageContext.Provider, { value: value, children: children }));
};
// Hook to use language context
export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error("useLanguage must be used within a LanguageProvider");
    }
    return context;
};
// Export helper functions for use in other components
export { isSeoLandingPage, getAlternateSeoUrl, getFallbackUrl, seoServiceSlugMap };
export default LanguageContext;
